import { createContext, useContext, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useJwtManager } from "@getbread/wheat-jwt-manager";
import type { FCWithChildren } from "../../api-client/resources/types/sharedTypes";
import { logger } from "../../logger";
import { jwtStorageKey } from "../../apiClientProvider";

export type AuthenticationContextValue = {
	loggedIn: boolean;
	logIn: (jwt: string) => void;
	logOut: () => void;
	buyerId: string;
	setMerchantName: (merchant: string) => void;
	selectedMerchant?: string;
};

const AuthenticationContext = createContext<AuthenticationContextValue | undefined>(undefined);

const AuthenticationProvider: FCWithChildren = ({ children }) => {
	const {
		payload: jwtPayload,
		setJwt,
		removeJwt,
	} = useJwtManager<{
		buyerID: string;
	}>({ storageKey: jwtStorageKey });

	// if we have a JWT, extract the buyer ID from that
	const buyerId: string | null = useMemo(() => jwtPayload?.buyerID ?? null, [jwtPayload]);

	const [selectedMerchant, setSelectedMerchant] = useState<string>();

	const queryClient = useQueryClient();

	const setMerchantName = (merchant: string) => {
		setSelectedMerchant(merchant);
	};

	const contextValue = useMemo(() => {
		const logIn = (jwt: string) => {
			setJwt(jwt);
		};

		const logOut = () => {
			removeJwt();
			queryClient.clear();
			window.sessionStorage.clear();
		};

		const loggedIn = !!jwtPayload;
		logger.addCommonProperties({ buyerId: loggedIn ? buyerId : null });

		return {
			loggedIn,
			logIn,
			logOut,
			buyerId: buyerId!,
			setMerchantName,
			selectedMerchant,
		};
	}, [buyerId, removeJwt, jwtPayload, queryClient, selectedMerchant, setJwt]);

	return (
		<AuthenticationContext.Provider value={contextValue}>
			{children}
		</AuthenticationContext.Provider>
	);
};

function useAuthentication(): AuthenticationContextValue {
	const context = useContext(AuthenticationContext);

	if (!context) {
		throw new Error("useAuthentication must be used within a context");
	}

	return context;
}

export { AuthenticationProvider, useAuthentication };
